import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@loot-os/ui/components/collapsible";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@loot-os/ui/components/sidebar";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";

/**
 * A sidebar row with pages folded beneath it: the row is its own link, the chevron beside it
 * folds and unfolds the list. The list opens whenever navigation lands on the row or one of its
 * pages (on load, or from a link elsewhere), so the section a page belongs to is visible;
 * folding it by hand sticks until the next arrival. Controlled, because the layout stays
 * mounted across routes and Base UI refuses a `defaultOpen` that changes after mount. Callers
 * render the `SidebarMenuSubItem`s.
 */
export function NavSubmenu({
  label,
  icon: Icon,
  active,
  link,
  badge,
  children,
}: {
  label: string;
  icon: LucideIcon;
  /** True while the row or one of its pages is the current page: highlights the row, unfolds the list. */
  active: boolean;
  /** The row's own link, rendered as the menu button. */
  link: ReactElement;
  /** Rendered after the label, before the chevron. */
  badge?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <SidebarMenuButton tooltip={label} isActive={active} render={link}>
          <Icon />
          <span className="truncate">{label}</span>
          {badge}
        </SidebarMenuButton>
        <CollapsibleTrigger render={<SidebarMenuAction />}>
          <ChevronRight className="transition-transform duration-200 group-data-open/collapsible:rotate-90" />
          <span className="sr-only">Toggle {label}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>{children}</SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

/**
 * A section row inside a submenu with its own pages folded beneath it, one level deeper
 * (Storefront → Live breaks → Hosts): `NavSubmenu`'s contract at the submenu's scale. The row is
 * its own link, the chevron folds the list, and arriving on the row or one of its pages unfolds
 * it. Callers render the `SidebarMenuSubItem`s.
 */
export function NavSubSection({
  label,
  active,
  link,
  badge,
  children,
}: {
  label: string;
  /** True while the row or one of its pages is the current page: highlights the row, unfolds the list. */
  active: boolean;
  /** The row's own link, rendered as the submenu button. */
  link: ReactElement;
  /** Rendered after the label, before the chevron. */
  badge?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/sub-collapsible">
      <SidebarMenuSubItem>
        {/* Right padding keeps the row's status dot clear of the chevron. */}
        <SidebarMenuSubButton isActive={active} render={link} className="pr-8">
          <span className="truncate">{label}</span>
          {badge}
        </SidebarMenuSubButton>
        <CollapsibleTrigger className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-md text-sidebar-foreground ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0">
          <ChevronRight className="transition-transform duration-200 group-data-open/sub-collapsible:rotate-90" />
          <span className="sr-only">Toggle {label}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="mr-0 ml-2.5">{children}</SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  );
}
